<template>
    <section class="event-list" aria-label="Event list">
        <div class="datatable-toolbar event-toolbar">
            <div class="event-search">
                <label class="sr-only" for="event-search">Search events</label>
                <input id="event-search" type="search" class="form-control" placeholder="Search events" v-model="dataFilter.searchText" @keyup.enter="doFind">
                <button class="btn btn-outline-primary" type="button" @click="doFind">Search</button>
                <button v-if="dataFilter.searchText" class="btn btn-link text-secondary clear-search" type="button" @click="clearSearch">Clear</button>
            </div>

            <RouterLink to="/event/add" class="btn btn-primary add-event-button">
                <i class="pi pi-plus mr-2" aria-hidden="true"></i>
                Add New
            </RouterLink>
        </div>

        <div class="table-responsive table-container event-table-wrapper">
            <table class="table table-striped event-table">

            <thead>
            <tr>
                <th scope="col">No</th>
                <th scope="col">Name</th>
                <th scope="col">Start Date</th>
                <th scope="col">End Date</th>
                <th scope="col">Location</th>
                <th scope="col">Status</th>
                <th scope="col">Actions</th>
            </tr>
            </thead>

            <tbody>
            <tr v-if="isLoading">
                <td colspan="7" class="table-message">
                    <span class="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                    Loading events…
                </td>
            </tr>
            <tr v-else-if="dataList.length === 0">
                <td colspan="7" class="table-message empty-state">
                    <i class="pi pi-calendar-times" aria-hidden="true"></i>
                    <strong>{{ dataFilter.searchText ? 'No events match your search.' : 'No events yet.' }}</strong>
                    <span>{{ dataFilter.searchText ? 'Try a different keyword or clear the search.' : 'Create an event to begin managing your programme.' }}</span>
                    <button v-if="dataFilter.searchText" class="btn btn-outline-primary btn-sm" type="button" @click="clearSearch">Clear search</button>
                </td>
            </tr>
            <template v-else>
            <tr v-for="(data, index) in dataList" :key="data.id">
                <td class="order-number">{{ index + 1 }}</td>
                <td class="text-left event-name">{{ data.name }}</td>
                <td>{{ fmtDate(data.startDate) }}</td>
                <td>{{ fmtDate(data.endDate) }}</td>
                <td class="text-left event-location">{{ data.locationName }}</td>
                <td>
                    <span class="badge badge-dark" v-if="data.status !== 1"> {{ data.statusLabel }}</span>
                    <span class="badge badge-success" v-if="data.status === 1"> {{ data.statusLabel }}</span>
                </td>
                <td class="actions">
                    <button type="button" class="btn btn-outline-success" aria-label="Activate event" v-tooltip="'Activate event'" @click="onActivate(data)" v-if="data.status !== 1"><i class="pi pi-verified" aria-hidden="true"></i></button>
                    <button type="button" class="btn btn-outline-secondary" aria-label="Deactivate event" v-tooltip="'Deactivate event'" @click="onDeactivated(data)" v-if="data.status === 1"><i class="pi pi-ban" aria-hidden="true"></i></button>
                    <button type="button" class="btn btn-outline-secondary" aria-label="Edit event" v-tooltip="'Edit event'" @click="onEdit(data)"><i class="pi pi-pencil" aria-hidden="true"></i></button>
                    <button type="button" class="btn btn-outline-danger" aria-label="Remove event" v-tooltip="'Remove event'" @click="onRemove(data)"><i class="pi pi-trash" aria-hidden="true"></i></button>
                </td>
            </tr>
            </template>
            </tbody>

        </table>
    </div>

    <ConfirmDialog></ConfirmDialog>
    </section>
</template>

<script>
import {defineComponent} from 'vue'
import apiService from "@/services/ApiService";
import moment from "moment";

export default defineComponent({
    name: "EventList",
    data() {
        return {
            dataList: [],
            isLoading: true,
            dataFilter: {
                searchText: ''
            }
        }
    },
    methods: {
        onEdit(data) {
            this.$router.push(`/event/edit/${data.id}`);
        },
        onRemove(data) {
            this.$confirm.require({
                message: 'Are you sure you want to remove ?',
                header: 'Confirmation',
                icon: 'pi pi-exclamation-triangle',
                accept: () => {
                    this.doRemove(data.id);
                }
            });
        },
        onActivate(data) {
            console.log(data);
            apiService.post(`/event/${data.id}/activate`)
                .then(() => {
                    this.doFind();

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Event has been activated',
                        life: 5000
                    });


                }).catch(error => {
                console.log(error);
                this.$toast.add({
                    severity: 'error',
                    summary: 'Failed to activate event',
                    detail: error.response.data.Message,
                    life: 5000
                });
            });
        },
        onDeactivated(data) {
            console.log(data);

            apiService.post(`/event/${data.id}/deactivate`)
                .then(() => {
                    this.doFind();

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Event has been deactivated',
                        life: 5000
                    });


                }).catch(error => {
                console.log(error);
                this.$toast.add({
                    severity: 'error',
                    summary: 'Failed to deactivate event',
                    detail: error.response.data.Message,
                    life: 5000
                });
            });
            
        },
        doFind() {
            const params = {
                SearchText: this.dataFilter.searchText
            };
            this.isLoading = true;
            apiService.get('/event', {params: params})
                .then(response => {
                    this.dataList = response.data.data || [];
                }).catch(error => {
                    console.log(error);

                this.$toast.add({
                    severity: 'error',
                    summary: 'Failed to get event',
                        detail: error.response.data.Message,
                        life: 5000
                    });
                }).finally(() => {
                    this.isLoading = false;
                });
        },
        clearSearch() {
            this.dataFilter.searchText = '';
            this.doFind();
        },
        doRemove(id) {
            apiService.delete(`/event/${id}`)
                .then(() => {
                    this.doFind();

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Event has been removed',
                        life: 5000
                    });


                }).catch(error => {
                console.log(error);
                this.$toast.add({
                    severity: 'error',
                    summary: 'Failed to remove event',
                    detail: error.response.data.Message,
                    life: 5000
                });
            });
        },
        fmtDate(date) {
            return date && moment(date).isValid() ? moment(date).format("DD/MM/YYYY") : '—';
        },
    },
    mounted() {
        this.doFind();
    }
})
</script>

<style scoped>
.event-toolbar {
    align-items: center;
    display: flex;
    gap: 0.75rem;
    justify-content: space-between;
    margin-bottom: 0.5rem;
}

.event-search {
    align-items: center;
    display: flex;
    flex: 1;
    flex-wrap: wrap;
    gap: 0.5rem;
}

.event-search .form-control {
    max-width: 19rem;
}

.clear-search {
    font-size: 0.875rem;
    padding-left: 0.25rem;
    padding-right: 0.25rem;
}

.add-event-button {
    white-space: nowrap;
}

.event-table-wrapper {
    margin: 0;
}

.event-table th {
    white-space: nowrap;
}

.event-name {
    font-weight: 600;
    min-width: 17rem;
}

.event-location {
    min-width: 10rem;
}

.table-message {
    color: #6c757d;
    padding: 2.5rem 1rem !important;
    text-align: center;
}

.empty-state > * {
    display: block;
    margin: 0 auto;
}

.empty-state > .pi {
    color: #426cc6;
    font-size: 1.75rem;
    margin-bottom: 0.75rem;
}

.empty-state > strong {
    color: #343a40;
    margin-bottom: 0.35rem;
}

.empty-state > span {
    margin-bottom: 1rem;
}

@media (max-width: 767.98px) {
    .event-toolbar {
        align-items: stretch;
        flex-direction: column;
    }

    .event-search .form-control {
        max-width: none;
        width: 100%;
    }

    .add-event-button {
        align-self: flex-start;
    }
}
</style>
